import { Helmet } from "react-helmet-async";

const SITE_URL =
  process.env.REACT_APP_SITE_URL || "https://www.cameronlowegolf.com";
const siteBase = SITE_URL.replace(/\/$/, "");
const ogImage = `${siteBase}/cameron-lowe-golf.jpg`;

const DEFAULT_SEO = {
  title: "Cameron Lowe Golf | Amateur Golfer in Surrey, UK",
  description:
    "Follow Cameron Lowe — amateur golfer from Surrey, UK. Tournament performances, stats, equipment, sponsors, and news.",
  ogTitle: "Cameron Lowe Golf | Amateur Golfer",
  ogDescription:
    "Tournament results, performance stats, and insights from Cameron Lowe's amateur golf journey.",
  twitterTitle: "Cameron Lowe Golf | Amateur Golfer in Surrey",
  twitterDescription:
    "Tournament results, stats, and equipment from Cameron Lowe's golf journey.",
};

// Per-slug overrides. Keys must match the URL slugs registered in App.js
// and the SECTION_IDS map in HomePage.js.
const SLUG_SEO = {
  about: {
    title: "About | Cameron Lowe Golf",
    description:
      "About Cameron Lowe — amateur golfer from Surrey, UK. Background, goals, and career highlights.",
    ogTitle: "About Cameron Lowe | Amateur Golfer in Surrey",
    ogDescription:
      "Background and story of Cameron Lowe, amateur golfer from Surrey, UK.",
    twitterTitle: "About Cameron Lowe | Amateur Golfer",
    twitterDescription:
      "Background and story of Cameron Lowe, amateur golfer from Surrey, UK.",
  },
  equipment: {
    title: "Equipment | Cameron Lowe Golf",
    description:
      "What's in the bag — current clubs, balls and gear used by Cameron Lowe on tour.",
    ogTitle: "Equipment in the Bag | Cameron Lowe Golf",
    ogDescription:
      "What's in Cameron Lowe's bag — current clubs, balls and gear.",
    twitterTitle: "What's in the Bag | Cameron Lowe",
    twitterDescription: "Current clubs, balls and gear used by Cameron Lowe.",
  },
  sponsors: {
    title: "Sponsors | Cameron Lowe Golf",
    description:
      "Sponsors and partners supporting Cameron Lowe's amateur golf career.",
    ogTitle: "Sponsors | Cameron Lowe Golf",
    ogDescription:
      "Sponsors and partners supporting Cameron Lowe's amateur golf career.",
    twitterTitle: "Sponsors | Cameron Lowe",
    twitterDescription:
      "Sponsors and partners supporting Cameron Lowe's amateur golf career.",
  },
  tournaments: {
    title: "Tournaments | Cameron Lowe Golf",
    description:
      "Tournament schedule and past results from Cameron Lowe's amateur golf events.",
    ogTitle: "Tournaments & Results | Cameron Lowe Golf",
    ogDescription:
      "Upcoming events and past tournament results for Cameron Lowe.",
    twitterTitle: "Tournaments | Cameron Lowe",
    twitterDescription:
      "Upcoming events and past tournament results for Cameron Lowe.",
  },
  stats: {
    title: "Stats | Cameron Lowe Golf",
    description:
      "Performance stats, scoring data, and tournament metrics for Cameron Lowe.",
    ogTitle: "Stats & Performance | Cameron Lowe Golf",
    ogDescription:
      "Performance stats, scoring data, and tournament metrics for Cameron Lowe.",
    twitterTitle: "Stats | Cameron Lowe",
    twitterDescription:
      "Performance stats, scoring data, and tournament metrics for Cameron Lowe.",
  },
};

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "Person",
  name: "Cameron Lowe",
  url: `${siteBase}/`,
  description:
    "Amateur golfer from Surrey, UK. Tournament results, stats, and equipment.",
  jobTitle: "Amateur Golfer",
  sameAs: [
    "https://www.instagram.com/camlowegolf/",
    "https://twitter.com/cameronlowegolf",
    "https://www.youtube.com/channel/UC1QY6uVH3w5Z0t8xHJxXs1Q",
  ],
};

/**
 * Per-route SEO for the public home page and its section sub-routes.
 * Pass `slug` to emit the overrides for /about, /equipment, /sponsors,
 * /tournaments, /stats. With no slug, emits the default home-page meta
 * plus the Person JSON-LD (we only want that on the canonical home URL).
 */
export default function HomeSeo({ slug }) {
  const meta = (slug && SLUG_SEO[slug]) || DEFAULT_SEO;
  const canonical = slug ? `${siteBase}/${slug}` : `${siteBase}/`;

  return (
    <Helmet>
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <link rel="canonical" href={canonical} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={meta.ogTitle} />
      <meta property="og:description" content={meta.ogDescription} />
      <meta property="og:image" content={ogImage} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonical} />
      <meta name="twitter:title" content={meta.twitterTitle} />
      <meta name="twitter:description" content={meta.twitterDescription} />
      <meta name="twitter:image" content={ogImage} />
      {!slug && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}
